import { InfoOutlined } from "@mui/icons-material";
import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { useModals } from "../../../../../../context/ModalContext";
import { JobApproved } from "@components/jobs/jobs-list/jobs-layout/components/Approved/JobApproved";
import type { BulkUpdateJobEventWorkforcePayload } from "@components/jobs/jobs-list/jobs-layout/actions/useUpdateJobEventWorkforce";
import { useBulkUpdateEventWorkforce } from "@components/jobs/jobs-list/hooks/useUpdate";
import { Job } from "@components/jobs/interfaces/job.interfaces";
import { JobEventForInvoice } from "@components/jobs/jobs-details/events/board/context";
import { colors, alphaVar } from "../../../../../ui/colors";
import { useGlobalSnackbar } from "src/context/SnackbarContext";

interface Props {
  job: Job;
  jobEvents: JobEventForInvoice[];
  modalKey: string;
  onDone?: () => void;
  initialApplyFromDate?: boolean;
}

type SelectedWorkforce = {
  supervisors: any[];
  employees: any[];
  contractors: any[];
};

const emptyWorkforce: SelectedWorkforce = {
  supervisors: [],
  employees: [],
  contractors: [],
};

function toBulkWorkforces(workforce: SelectedWorkforce): BulkUpdateJobEventWorkforcePayload[] {
  return [
    ...workforce.supervisors.map((worker) => ({
      uid: worker.uid,
      role: "supervisor" as const,
    })),
    ...workforce.employees.map((worker) => ({
      uid: worker.uid,
      role: "employee" as const,
    })),
    ...workforce.contractors.map((worker) => ({
      uid: worker.uid,
      role: "contractor" as const,
    })),
  ];
}

export function BulkEventWorkforceView({ job, jobEvents, modalKey, onDone, initialApplyFromDate = false }: Props) {
  const { closeByKey } = useModals();
  const bulkUpdateMutation = useBulkUpdateEventWorkforce();
  const { showSnackbar } = useGlobalSnackbar();
  const assignmentVersionRef = useRef(job.updatedAt);

  const [selectedWorkforce, setSelectedWorkforce] = useState<SelectedWorkforce>(emptyWorkforce);
  const [applyFromDate] = useState(initialApplyFromDate);
  const [startDate, setStartDate] = useState("");

  const workforces = toBulkWorkforces(selectedWorkforce);
  const isWorkforceEmpty = workforces.length === 0;
  const missingDate = applyFromDate && !startDate;

  const canSave = Boolean(assignmentVersionRef.current) && !bulkUpdateMutation.isPending && (applyFromDate ? Boolean(startDate) : jobEvents.length > 0);

  const warningMessage = isWorkforceEmpty ? "Saving with nobody selected removes the workforce from these visits." : "";

  const handleSave = async () => {
    if (!canSave) return;

    const payload = applyFromDate
      ? {
          jobId: job.id,
          expectedJobUpdatedAt: assignmentVersionRef.current,
          startDate: new Date(`${startDate}T00:00:00`).toISOString(),
          workforces,
        }
      : {
          jobId: job.id,
          expectedJobUpdatedAt: assignmentVersionRef.current,
          eventUids: jobEvents.map((event) => event.uid),
          workforces,
        };

    try {
      const result: any = await bulkUpdateMutation.mutateAsync(payload);
      const warning = result?.warning || result?.data?.warning;
      const assignmentVersion = result?.assignmentVersion || result?.data?.assignmentVersion;
      if (assignmentVersion) assignmentVersionRef.current = assignmentVersion;
      if (warning) showSnackbar(warning, "warning");
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || (error?.response?.status === 409 ? "Assignments changed. Reload the job before saving." : "Workforce could not be saved."), "error");
      return;
    }

    onDone?.();
    closeByKey(modalKey);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 900, color: colors.gray[900], lineHeight: 1.2 }}>
            {applyFromDate ? "Edit workforce by date" : "Edit workforce for selected visits"}
          </Typography>

          <Typography sx={{ mt: 0.45, fontSize: "0.8rem", fontWeight: 600, color: colors.gray[500] }}>
            {applyFromDate
              ? "Choose a start date and select the workforce to apply."
              : `${jobEvents.length} selected visit${jobEvents.length === 1 ? "" : "s"}`}
          </Typography>
        </Box>

        {applyFromDate && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "flex-start", sm: "flex-end" },
              gap: 0.55,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: alphaVar(colors.orange[100], 0.45),
                borderRadius: "999px",
                px: 1.25,
                py: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.74rem",
                  fontWeight: 900,
                  color: colors.orange[700],
                  whiteSpace: "nowrap",
                }}
              >
                Start date
              </Typography>

              <TextField
                type="date"
                size="small"
                value={startDate}
                error={missingDate}
                onChange={(event) => setStartDate(event.target.value)}
                inputProps={{
                  min: new Date().toISOString().slice(0, 10),
                }}
                sx={{
                  width: 155,
                  "& .MuiOutlinedInput-root": {
                    height: 30,
                    borderRadius: "999px",
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    fontSize: "0.74rem",
                    fontWeight: 800,
                    color: colors.gray[700],

                    "& fieldset": {
                      border: missingDate ? `1px solid ${colors.orange[600]}` : "none",
                    },

                    "&:hover fieldset": {
                      border: missingDate ? `1px solid ${colors.orange[600]}` : "none",
                    },

                    "&.Mui-focused fieldset": {
                      border: missingDate ? `1px solid ${colors.orange[600]}` : "none",
                    },
                  },

                  "& input": {
                    py: 0,
                    px: 1.1,
                  },
                }}
              />
            </Box>

            <Typography
              sx={{
                pr: { xs: 0, sm: 1 },
                fontSize: "0.72rem",
                fontWeight: 800,
                color: missingDate ? colors.orange[700] : colors.gray[400],
                lineHeight: 1.2,
                textAlign: { xs: "left", sm: "right" },
              }}
            >
              {missingDate ? "Select a start date to enable Save." : "Visits on or after this date will be updated."}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
        <JobApproved mode="create" currentWf={[]} onWorkforceChange={setSelectedWorkforce} />
      </Box>

      <Divider />

      <Box sx={{ px: 2.5, py: 2, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
        {warningMessage && (
          <Typography sx={{ mr: "auto", fontSize: "0.75rem", fontWeight: 700, color: colors.gray[500] }}>{warningMessage}</Typography>
        )}
        <Button
          variant="text"
          onClick={() => closeByKey(modalKey)}
          sx={{
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 800,
            color: colors.gray[500],
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={!canSave}
          onClick={handleSave}
          sx={{
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 900,
            bgcolor: colors.orange.base,
            color: "#fff",
            boxShadow: "none",

            "&:hover": {
              bgcolor: colors.orange[600],
              boxShadow: "none",
            },

            "&.Mui-disabled": {
              bgcolor: alphaVar(colors.orange[100], 0.55),
              color: alphaVar(colors.orange[700], 0.45),
            },
          }}
        >
          {bulkUpdateMutation.isPending ? "Saving..." : "Save workforce"}
        </Button>
      </Box>
    </Box>
  );
}
